// app/src/components/Search/Search.tsx
import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppContext } from '../../Context'
import { API_URL } from '../../util'
import styles from './Search.module.css'

type SearchResult = {
  _id: string
  name?: string
  title?: string
  description?: string
  excerpt?: string
  path: string
  slug?: string
  score: number
}

type SearchResponse = {
  frameworks: SearchResult[]
  frameworkPages: SearchResult[]
  pages: SearchResult[]
  posts: SearchResult[]
}

function highlightMatch(text: string, query: string): React.ReactNode {
  if (!text || !query) return text
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'))
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase()
      ? <mark key={i} className={styles.highlight}>{part}</mark>
      : part
  )
}

function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  )
}

function Search() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const mobileInputRef = useRef<HTMLInputElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()
  const navigate = useNavigate()
  const context = useAppContext()

  const doSearch = useCallback(async (q: string) => {
    if (q.length < 2) {
      setResults(null)
      return
    }
    setLoading(true)
    setError(false)
    try {
      const headers: Record<string, string> = {}
      if (context.user) {
        headers.Authorization = `Token ${context.user.token}`
      }
      const res = await fetch(`${API_URL}site-search?q=${encodeURIComponent(q)}`, { headers })
      if (!res.ok) throw new Error('Search failed')
      const data: SearchResponse = await res.json()
      setResults(data)
    } catch {
      setError(true)
      setResults(null)
    } finally {
      setLoading(false)
    }
  }, [context.user])

  const handleInput = (value: string) => {
    setQuery(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => doSearch(value), 300)
  }

  const close = () => {
    setMobileOpen(false)
    setQuery('')
    setResults(null)
    setError(false)
  }

  const handleNavigate = (path: string) => {
    close()
    navigate(path)
  }

  // Click outside to close
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        close()
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Escape to close
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [])

  useEffect(() => {
    if (mobileOpen) mobileInputRef.current?.focus()
  }, [mobileOpen])

  const hasResults = results && (results.frameworks.length > 0 || (results.frameworkPages?.length ?? 0) > 0 || results.pages.length > 0 || results.posts.length > 0)
  const noResults = results && !hasResults && query.length >= 2

  const showDropdown = loading || error || noResults || hasResults
  const dropdown = showDropdown ? (
    <div className={styles.dropdown}>
      {loading && (
        <div className={styles.message}><span className={styles.spinner} /></div>
      )}
      {error && (
        <div className={styles.message}>Something went wrong, try again</div>
      )}
      {noResults && !loading && (
        <div className={styles.message}>No results found for "{query}"</div>
      )}
      {hasResults && !loading && (
        <>
          {results!.frameworks.length > 0 && (
            <div className={styles.group}>
              <div className={styles.groupHeader}>Benchmark Wizard</div>
              {results!.frameworks.map(r => (
                <a key={r._id} className={styles.resultItem} onClick={() => handleNavigate(r.path)}>
                  <div className={styles.resultTitle}>{highlightMatch(r.name ?? '', query)}</div>
                  {r.description && <div className={styles.resultExcerpt}>{highlightMatch(r.description, query)}</div>}
                </a>
              ))}
            </div>
          )}
          {(results!.frameworkPages?.length ?? 0) > 0 && (
            <div className={styles.group}>
              <div className={styles.groupHeader}>Frameworks &amp; Suppliers</div>
              {results!.frameworkPages.map(r => (
                <a key={r._id} className={styles.resultItem} onClick={() => handleNavigate(r.path)}>
                  <div className={styles.resultTitle}>{highlightMatch(r.title ?? '', query)}</div>
                  {r.excerpt && <div className={styles.resultExcerpt}>{highlightMatch(r.excerpt, query)}</div>}
                </a>
              ))}
            </div>
          )}
          {results!.pages.length > 0 && (
            <div className={styles.group}>
              <div className={styles.groupHeader}>Pages</div>
              {results!.pages.map(r => (
                <a key={r._id} className={styles.resultItem} onClick={() => handleNavigate(r.path)}>
                  <div className={styles.resultTitle}>{highlightMatch(r.title ?? '', query)}</div>
                  {r.excerpt && <div className={styles.resultExcerpt}>{highlightMatch(r.excerpt, query)}</div>}
                </a>
              ))}
            </div>
          )}
          {results!.posts.length > 0 && (
            <div className={styles.group}>
              <div className={styles.groupHeader}>Blog Posts</div>
              {results!.posts.map(r => (
                <a key={r._id} className={styles.resultItem} onClick={() => handleNavigate(r.path)}>
                  <div className={styles.resultTitle}>{highlightMatch(r.title ?? '', query)}</div>
                  {r.excerpt && <div className={styles.resultExcerpt}>{highlightMatch(r.excerpt, query)}</div>}
                </a>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  ) : null

  return (
    <div ref={wrapperRef}>
      {/* Desktop: always show input in top bar */}
      <div className={styles.desktopOnly}>
        <div className={styles.wrapper}>
          <div className={styles.inputContainer} onClick={() => inputRef.current?.focus()}>
            <SearchIcon />
            <input
              ref={inputRef}
              className={styles.input}
              type="text"
              placeholder="Search..."
              value={query}
              onChange={e => handleInput(e.target.value)}
            />
            {query && <button className={styles.clearButton} onClick={close}>✕</button>}
          </div>
          {dropdown}
        </div>
      </div>

      {/* Mobile: icon triggers overlay */}
      <div className={styles.mobileOnly}>
        <button className={styles.iconButton} onClick={() => setMobileOpen(true)} aria-label="Search">
          <SearchIcon />
        </button>
        {mobileOpen && (
          <div className={styles.mobileOverlay}>
            <div className={styles.inputContainer}>
              <SearchIcon />
              <input
                ref={mobileInputRef}
                className={styles.input}
                type="text"
                placeholder="Search..."
                value={query}
                onChange={e => handleInput(e.target.value)}
              />
              {query && <button className={styles.clearButton} onClick={() => { setQuery(''); setResults(null) }}>✕</button>}
            </div>
            <button className={styles.mobileCloseButton} onClick={close}>✕</button>
            {dropdown}
          </div>
        )}
      </div>
    </div>
  )
}

export { Search }
